(function (root) {
  'use strict';
  const normalize = value => String(value || '').trim().toLowerCase();
  const unique = codes => [...new Map(codes.filter(Boolean).map(code => [normalize(code), String(code).trim()])).values()];
  function applications(cart) {
    return [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))].filter(Boolean);
  }
  function codesIn(cart) {
    return unique([...(cart.discount_codes || []).map(entry => entry.code), ...applications(cart).filter(app => app.type === 'discount_code').map(app => app.code || app.title)]);
  }
  function isSelected(cart, code) {
    return codesIn(cart).some(value => normalize(value) === normalize(code));
  }
  function isApplied(cart, code) {
    return (cart.discount_codes || []).some(entry => normalize(entry.code) === normalize(code) && entry.applicable === true) || applications(cart).some(app => app.type === 'discount_code' && normalize(app.code || app.title) === normalize(code) && Number(app.total_allocated_amount || app.value || 0) > 0);
  }
  function activeDiscounts(cart) {
    const keys = applications(cart).map(app => JSON.stringify([app.type, normalize(app.code || app.title), app.target_type || '', app.value_type || '', String(app.value ?? '')]));
    (cart.discount_codes || []).filter(entry => entry.applicable === true).forEach(entry => keys.push('code:' + normalize(entry.code)));
    return new Set(keys);
  }
  function discountsChanged(before, after) {
    const current = activeDiscounts(after);
    return [...activeDiscounts(before)].some(key => !current.has(key));
  }
  function formatOfferDate(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {timeZone:'America/Los_Angeles',year:'numeric',month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZoneName:'short'}).format(date);
  }
  async function request(fetcher, url, body, signal = AbortSignal.timeout(12000)) {
    const response = await fetcher(url, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, ...(body ? {body: JSON.stringify(body)} : {}), signal});
    if (!response.ok) {const error=new Error('cart_request_failed');error.status=response.status;throw error;}
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('cart_response_invalid');
    return cart;
  }
  async function changeCode(fetcher, base, code, sections, removing) {
    if (!code || code.includes(',')) throw new Error('invalid_code');
    const signal = AbortSignal.timeout(12000);
    const before = await request(fetcher, base + 'cart.js', undefined, signal);
    const existing = codesIn(before);
    const body = {discount: (removing ? existing.filter(value => normalize(value) !== normalize(code)) : unique([...existing, code])).join(',')};
    if (sections.length) body.sections = sections;
    let cart;
    try {cart = await request(fetcher, base + 'cart/update.js', body, signal);}
    catch(error) {error.updateAttempted=true;error.before=before;throw error;}
    const selected=isSelected(cart,code),applied=isApplied(cart,code);
    return {before, cart, selected, applied, removed: !selected, changed: discountsChanged(before, cart), pending: selected&&!applied};
  }
  const applyCode = (fetcher, base, code, sections) => changeCode(fetcher, base, code, sections, false);
  const removeCode = (fetcher, base, code, sections) => changeCode(fetcher, base, code, sections, true);
  const CHECKOUT_MUTATION = 'mutation ProductOfferCheckout($input: CartInput!, $country: CountryCode) @inContext(country: $country) { cartCreate(input: $input) { cart { checkoutUrl discountCodes { code applicable } } userErrors { code field message } } }';
  function checkoutInput(formData, codes, country) {
    const variant=String(formData.get('id') || ''),quantity=Number(formData.get('quantity') || 1);
    if(!/^\d+$/.test(variant)||!Number.isSafeInteger(quantity)||quantity<1)throw new Error('checkout_invalid_product');
    const line={merchandiseId:'gid://shopify/ProductVariant/'+variant,quantity};
    const plan=String(formData.get('selling_plan') || '');
    if(plan){if(!/^\d+$/.test(plan))throw new Error('checkout_invalid_plan');line.sellingPlanId='gid://shopify/SellingPlan/'+plan;}
    const attributes=[];
    for(const [name,value] of formData){
      const match=name.match(/^properties\[(.+)\]$/);
      if(match){if(typeof value!=='string')throw new Error('checkout_file_property');attributes.push({key:match[1],value});}
      if(name.startsWith('shopify-additional-items['))throw new Error('checkout_additional_items');
    }
    if(attributes.length)line.attributes=attributes;
    return {lines:[line],discountCodes:unique(codes),...(/^[A-Z]{2}$/.test(country||'')?{buyerIdentity:{countryCode:country}}:{})};
  }
  async function createCheckout(fetcher, formData, codes, country) {
    const input=checkoutInput(formData,codes,country);
    const response=await fetcher('/api/2026-10/graphql.json',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({query:CHECKOUT_MUTATION,variables:{input,country:input.buyerIdentity?.countryCode || null}}),signal:AbortSignal.timeout(12000)});
    if(!response.ok)throw new Error('checkout_request_failed');
    const result=await response.json(),payload=result.data?.cartCreate;
    if(result.errors?.length||payload?.userErrors?.length||!payload?.cart?.checkoutUrl)throw new Error('checkout_create_failed');
    return payload.cart;
  }
  function timeWindow(dataset, now) {
    const start=dataset.start ? Date.parse(dataset.start) : null;
    const end=dataset.end ? Date.parse(dataset.end) : null;
    return {hidden:(start!==null&&(!Number.isFinite(start)||now<start))||(end!==null&&(!Number.isFinite(end)||now>=end)),next:Math.min(...[start,end].filter(value=>Number.isFinite(value)&&value>now),Infinity)};
  }
  const api = {normalize, unique, applications, codesIn, isSelected, isApplied, activeDiscounts, discountsChanged, formatOfferDate, applyCode, removeCode, timeWindow, CHECKOUT_MUTATION, checkoutInput, createCheckout};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || root.customElements.get('lr-product-offers')) return;
  let cartQueue = Promise.resolve();
  class ProductOffers extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.connectionRevision=(this.connectionRevision || 0)+1;
      this.busy = false;
      this.cartReady = false;
      this.syncing = false;
      this.pendingCode = null;
      this.pendingSections = null;
      this.reconcileAction = null;
      this.errors = new Map();
      this.savedCodes=[];
      this.checkoutBusy=false;
      this.checkoutGroups=[];
      this.cartRevision = 0;
      this.base = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.text = JSON.parse(this.querySelector('[data-offer-messages]').textContent);
      this.status = this.querySelector('[data-offer-status]');
      this.setupCampaignGroups();
      this.querySelectorAll('[data-offer-end]').forEach(node => {node.textContent=formatOfferDate(node.dateTime);});
      const serverNow=Number(this.dataset.now)*1000;
      this.clockOffset=Number.isFinite(serverNow)&&serverNow>0 ? serverNow-root.performance.now() : null;
      this.addEventListener('click', event => this.onClick(event), {signal: this.controller.signal});
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {if (event.target === dialog) {const box=dialog.getBoundingClientRect(); if(event.clientX<box.left||event.clientX>box.right||event.clientY<box.top||event.clientY>box.bottom) dialog.close();}}, {signal:this.controller.signal});
      });
      ['cart:updated', 'cart:refresh', 'ajaxProduct:added'].forEach(name => document.addEventListener(name, event => this.onCartUpdate(event.detail), {signal:this.controller.signal}));
      ['pageshow','focus'].forEach(name=>root.addEventListener(name,()=>this.scheduleRefresh(),{signal:this.controller.signal}));
      ['pageshow','focus'].forEach(name=>root.addEventListener(name,()=>this.syncVisibility(),{signal:this.controller.signal}));
      document.addEventListener('visibilitychange',()=>this.syncVisibility(),{signal:this.controller.signal});
      this.scope=this.closest('.product__info-container') || this.parentElement;
      this.setupCheckout();
      this.scope.addEventListener('change',()=>this.syncVisibility(),{signal:this.controller.signal});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => this.onCartUpdate(event));
        if(PUB_SUB_EVENTS.variantChange)this.unsubscribeVariant=subscribe(PUB_SUB_EVENTS.variantChange,event=>{if(event?.data?.sectionId===this.dataset.section)this.syncVisibility(event.data.variant?.id);});
      }
      this.syncVisibility();
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.connectionRevision++;
      this.refreshRevision=(this.refreshRevision || 0)+1;
      this.unsubscribeCart?.(); this.unsubscribeVariant?.();
      clearTimeout(this.timer); clearTimeout(this.refreshTimer);
      this.checkoutGroups?.forEach(group=>{group.native.hidden=group.hidden;group.native.inert=group.inert;delete group.native.dataset.offerCheckoutHidden;group.box.remove();});
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    }
    now() {return this.clockOffset===null ? Date.now() : this.clockOffset+root.performance.now();}
    setupCampaignGroups() {
      if (this.campaignGroups) return;
      this.campaignGroups=[];
      let groups;
      try {groups=JSON.parse(this.querySelector('[data-offer-groups]')?.textContent || '[]');} catch (_) {return;}
      if (!Array.isArray(groups)) return;
      const list=this.querySelector('.lr-offers__list');
      const offers=new Map([...this.querySelectorAll('[data-offer][data-native-id]')].map(offer=>[offer.dataset.nativeId,offer]));
      const assigned=new Set();
      groups.forEach(group=>{
        if (!group || typeof group.title!=='string' || !group.title.trim() || !Array.isArray(group.members)) return;
        // Only use offers that passed the server's native product/customer/currency checks.
        const membership=new Set(group.members);
        const members=[...offers].filter(([id])=>membership.has(id)&&!assigned.has(id)).map(([,offer])=>offer);
        if (!members.length) return;
        const id=members[0].dataset.dialog+'-campaign';
        const ticket=document.createElement('li');ticket.className='lr-offers__ticket';ticket.dataset.campaign='';
        const button=document.createElement('button');button.type='button';button.className='lr-offers__details';
        button.dataset.offerDetails='';button.setAttribute('aria-haspopup','dialog');button.setAttribute('aria-controls',id);
        const title=document.createElement('span');title.className='lr-offers__title';title.textContent=group.title;
        const icon=members[0].querySelector('.lr-offers__details-icon');if(icon)title.append(icon.cloneNode(true));
        button.append(title);ticket.append(button);
        const dialog=document.createElement('dialog');dialog.id=id;dialog.className='lr-offers__dialog lr-offers__campaign-dialog';dialog.setAttribute('aria-labelledby',id+'-title');
        const inner=document.createElement('div');inner.className='lr-offers__dialog-inner';
        const close=members[0].querySelector('[data-offer-close]').cloneNode(true);
        const heading=document.createElement('h2');heading.id=id+'-title';heading.textContent=group.title;
        inner.append(close,heading);
        const rows=document.createElement('ul');rows.className='lr-offers__campaign-members';inner.append(rows);
        // Insert at the first member's position in the existing deterministic offer order.
        const first=[...list.children].find(node=>members.includes(node));list.insertBefore(ticket,first);
        members.forEach(member=>{
          assigned.add(member.dataset.nativeId);
          const original=member.querySelector('dialog');
          const content=original.querySelector('.lr-offers__dialog-inner');
          content.querySelector('[data-offer-close]').remove();
          const h2=content.querySelector('h2');const h3=document.createElement('h3');h3.textContent=h2.textContent;h2.replaceWith(h3);
          const terms=content.querySelector('dl');
          if (terms?.children.length) {
            const disclosure=document.createElement('details');disclosure.className='lr-offers__campaign-terms';
            const summary=document.createElement('summary');summary.textContent=this.text.conditions;
            disclosure.append(summary);terms.replaceWith(disclosure);disclosure.append(terms);
          } else terms?.remove();
          const header=document.createElement('div');header.className='lr-offers__campaign-row';
          h3.replaceWith(header);header.append(h3);
          const controls=content.querySelector('.lr-offers__dialog-code');if(controls)header.append(controls);
          // Reuse original code controls/error panels; keep one control per tier.
          member.replaceChildren(...content.childNodes);member.classList.add('lr-offers__campaign-member');
          member.dataset.dialog=id;rows.append(member);
        });
        if (typeof group.details==='string' && group.details.trim()) {
          const note=document.createElement('p');note.className='lr-offers__campaign-note';note.textContent=group.details;inner.append(note);
        }
        dialog.append(inner);ticket.append(dialog);
        this.campaignGroups.push({ticket,dialog,members});
      });
    }
    syncCampaignGroups() {
      (this.campaignGroups || []).forEach(({ticket,dialog,members})=>{
        ticket.hidden=!members.some(member=>!member.hidden);
        if (ticket.hidden && dialog.open) dialog.close();
      });
    }
    syncVisibility(variantId) {
      const selected=String(variantId || this.scope.querySelector('form[action*="/cart/add"] [name="id"]')?.value || this.dataset.variant);
      const subscription=Boolean(this.scope.querySelector('form[action*="/cart/add"] [name="selling_plan"]')?.value);
      const now = this.now();
      let next=Infinity;
      this.querySelectorAll('[data-offer]').forEach(offer => {
        const variants=(offer.dataset.variants || '').split(',').map(id=>id.trim()).filter(Boolean);
        const window=timeWindow(offer.dataset,now);
        next=Math.min(next,window.next);
        offer.hidden = (variants.length>0&&!variants.includes(selected)) || (subscription&&offer.dataset.oneTimeOnly==='true') || window.hidden;
        const dialog=this.querySelector('#'+CSS.escape(offer.dataset.dialog));
        if (offer.hidden && dialog?.open && !offer.classList.contains('lr-offers__campaign-member')) dialog.close();
      });
      this.syncCampaignGroups();
      this.hidden = !this.querySelector('[data-offer]:not([hidden])');
      this.updateCheckoutControls();
      clearTimeout(this.timer);
      this.timer=setTimeout(()=>this.syncVisibility(),Math.max(1,Math.min(30000,next-now+10)));
    }
    async onClick(event) {
      this.syncVisibility();
      if(event.target.closest('[data-offer]')?.hidden)return;
      const details = event.target.closest('[data-offer-details], [data-offer-error]');
      if (details) {const dialog=this.querySelector('#'+CSS.escape(details.getAttribute('aria-controls')));if(dialog&&!dialog.open)dialog.showModal();return;}
      const close=event.target.closest('[data-offer-close]');
      if (close) {close.closest('dialog').close();return;}
      const retry=event.target.closest('[data-offer-retry]');
      if(retry){
        const code=retry.dataset.offerRetry,issue=this.errors.get(normalize(code));
        if(!issue||this.busy||this.pendingCode||this.syncing||this.checkoutBusy)return;
        if(!this.cartReady||issue.action==='sync'){await this.refresh();return;}
        await this.changeOffer(code,issue.action==='remove',retry);return;
      }
      const button=event.target.closest('[data-offer-apply]');
      const removing=button?.type==='checkbox'&&button.dataset.selected==='true';
      if(button?.type==='checkbox')button.checked=button.dataset.selected==='true';
      if (!button || !this.cartReady || this.busy || this.pendingCode || this.checkoutBusy) return;
      await this.changeOffer(button.dataset.offerApply,removing,button);
    }
    async changeOffer(code,removing,button) {
      this.busy=true;
      this.refreshRevision=(this.refreshRevision || 0)+1;
      this.syncing=false;
      this.errors.delete(normalize(code));
      this.lastCode=code;
      this.lastAction=removing?'remove':'apply';
      this.applyDialog=removing?null:button.closest('dialog');
      this.updateControls();
      this.announce(removing?'':this.text.applying,code);
      const mini=document.querySelector('mini-cart');
      const sectionDefs=typeof mini?.getSectionsToRender === 'function' ? mini.getSectionsToRender().slice(0,5) : [];
      const connection=this.connectionRevision;
      let requestRevision;
      cartQueue = cartQueue.catch(()=>{}).then(()=>{
        if(!this.isConnected || connection!==this.connectionRevision)throw new Error('offer_disconnected');
        this.syncVisibility();
        if(button.closest('[data-offer]')?.hidden)throw new Error('offer_ended');
        requestRevision=this.cartRevision;
        return (removing?removeCode:applyCode)(root.fetch.bind(root),this.base,code,sectionDefs.map(item=>item.section || item.id));
      });
      try {
        const result=await cartQueue;
        if (!this.isConnected || connection!==this.connectionRevision) return;
        this.lastBeforeCart=result.before;
        if (requestRevision !== this.cartRevision) {
          this.pendingSections=sectionDefs;
          this.pendingCode=code;
          this.reconcileAction={code,removing};
          this.cartReady=false;
          this.lastResult=null;
          return;
        }
        this.lastResult=removing?(result.removed?'removed':'remove-failed'):result.selected?'selected':'ineligible';
        if(removing?!result.removed:!result.selected)this.setError(code,removing?'removeError':'applyError',removing?'remove':'apply');
        this.renderCart(result.cart);
        this.renderSections(result.cart.sections,sectionDefs);
        this.announce(removing ? (result.removed?'':this.text.error) : result.applied ? (result.changed ? this.text.appliedChanged : this.text.success) : result.pending ? this.text.pending : this.text.ineligible,code);
        document.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart:result.cart,source:'product-offers'}}));
        if(typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'product-offers',cartData:result.cart});
      } catch(error) {
        if(!this.isConnected || connection!==this.connectionRevision)return;
        if(error.updateAttempted){
          this.lastBeforeCart=error.before;
          this.pendingSections=sectionDefs;
          this.pendingCode=code;
          this.reconcileAction={code,removing};
          this.cartReady=false;
          this.lastResult=null;
          this.announce(this.text.syncing,code);
        } else {
          if(error.message!=='offer_ended')this.cartReady=false;
          this.setError(code,error.status===429?'rateLimit':'loadError','sync');
          this.announce(this.errorMessage(code),code);
        }
      } finally {
        if(connection===this.connectionRevision){
          this.busy=false;
          this.updateControls();
          if(this.pendingCode || this.pendingSections)this.scheduleRefresh();
        }
      }
    }
    onCartUpdate(detail) {
      if(detail?.source!=='product-offers')this.cartRevision++;
      this.scheduleRefresh();
    }
    renderSections(sections,definitions) {
      definitions.forEach(def => {
        const html=sections?.[def.section || def.id];
        const target=document.getElementById(def.id);
        if(typeof html!=='string'||!target)return;
        const content=new DOMParser().parseFromString(html,'text/html').querySelector(def.selector || '.shopify-section');
        if(content)target.innerHTML=content.innerHTML;
      });
    }
    announce(message,code=this.lastCode) {
      this.status.textContent=message;
      this.querySelectorAll('[data-dialog-status]').forEach(node=>{node.textContent=normalize(node.closest('[data-offer]').querySelector('[data-offer-apply]')?.dataset.offerApply)===normalize(code) ? message : '';});
    }
    setError(code,reason,action) {this.errors.set(normalize(code),{reason,action});}
    errorMessage(code) {const issue=this.errors.get(normalize(code));return issue ? (this.text[issue.reason] || this.text.error) : '';}
    updateControls() {
      const waiting=this.busy || Boolean(this.pendingCode) || this.checkoutBusy;
      const locked=waiting || !this.cartReady;
      this.setAttribute?.('aria-busy',String(waiting || this.syncing&&!this.cartReady));
      this.querySelectorAll('[data-offer-apply]').forEach(item=>{
        const active=normalize(item.dataset.offerApply)===normalize(this.pendingCode || this.lastCode);
        const pending=waiting?active:!this.cartReady&&this.syncing;
        const error=this.errors.has(normalize(item.dataset.offerApply));
        item.disabled=locked || (item.type!=='checkbox'&&item.dataset.selected==='true');
        item.indeterminate=!this.cartReady;
        item.dataset.unknown=String(!this.cartReady);
        item.dataset.pending=String(pending);
        item.dataset.blocked=String(locked&&!pending);
        item.dataset.error=String(error);
        item.setAttribute?.('aria-busy',String(pending));
        item.setAttribute?.('aria-invalid',String(error));
      });
      this.querySelectorAll('[data-offer-error]').forEach(button=>{
        button.hidden=!this.errors.has(normalize(button.dataset.offerError));
      });
      this.querySelectorAll('[data-offer-error-panel]').forEach(panel=>{
        const code=panel.dataset.offerErrorPanel,issue=this.errors.get(normalize(code));
        panel.hidden=!issue;
        panel.querySelector('[data-offer-error-message]').textContent=this.errorMessage(code);
        const retry=panel.querySelector('[data-offer-retry]');
        retry.textContent=!this.cartReady||issue?.action==='sync'?this.text.checkAgain:this.text.retry;
        retry.disabled=waiting||this.syncing;
      });
      this.updateCheckoutControls();
    }
    setupCheckout() {
      this.scope.querySelectorAll('form[action*="/cart/add"]').forEach(form=>{
        const native=form.querySelector('.shopify-payment-button');
        if(!native)return;
        const box=document.createElement('div');box.dataset.offerCheckout='';box.className='lr-offers-checkout';box.hidden=true;
        const button=document.createElement('button');button.type='button';button.className='button button--full-width';button.textContent=this.text.checkout || 'Buy it now';
        const error=document.createElement('p');error.className='lr-offers-checkout__error';error.setAttribute('role','alert');error.hidden=true;
        box.append(button,error);native.after(box);
        this.checkoutGroups.push({form,native,box,button,error,hidden:native.hidden,inert:native.inert});
        button.addEventListener('click',()=>this.startCheckout(form,button,error),{signal:this.controller.signal});
      });
    }
    updateCheckoutControls() {
      if(!this.checkoutGroups?.length)return;
      const selectedHere=Boolean(this.querySelector('[data-offer]:not([hidden]) [data-offer-apply][data-selected="true"]'));
      const useCheckout=this.cartReady&&selectedHere;
      const locked=!this.cartReady||this.busy||Boolean(this.pendingCode)||this.checkoutBusy;
      this.checkoutGroups.forEach(group=>{
        group.native.hidden=group.hidden||useCheckout;
        group.native.dataset.offerCheckoutHidden=String(group.hidden||useCheckout);
        group.native.inert=group.inert||locked;
        group.box.hidden=!useCheckout;
        group.button.disabled=locked||Boolean(group.form.querySelector('[name="id"]')?.disabled);
        group.button.setAttribute('aria-busy',String(this.checkoutBusy));
        group.button.textContent=this.checkoutBusy?(this.text.checkoutLoading || 'Opening checkout…'):(this.text.checkout || 'Buy it now');
      });
    }
    async startCheckout(form,button,error) {
      if(this.checkoutBusy||!this.cartReady||this.busy||this.pendingCode)return;
      const connection=this.connectionRevision;
      this.checkoutBusy=true;error.hidden=true;this.updateControls();
      try {
        const cart=await request(root.fetch.bind(root),this.base+'cart.js');
        if(!this.isConnected||connection!==this.connectionRevision)return;
        this.renderCart(cart);
        const checkout=await createCheckout(root.fetch.bind(root),new FormData(form),codesIn(cart),root.Shopify?.country);
        if(!this.isConnected||connection!==this.connectionRevision)return;
        const url=new URL(checkout.checkoutUrl,root.location.origin);
        if(url.protocol!=='https:'||![root.location.hostname,root.Shopify?.shop].includes(url.hostname))throw new Error('checkout_invalid_url');
        if(root.Shopify?.locale)url.searchParams.set('locale',root.Shopify.locale);
        root.location.assign(url.href);
      } catch(issue) {
        if(this.isConnected&&connection===this.connectionRevision){error.textContent=this.text.checkoutError || 'We couldn’t open checkout. Please try again.';error.hidden=false;}
      } finally {
        if(connection===this.connectionRevision){this.checkoutBusy=false;this.updateControls();}
      }
    }
    renderCart(cart) {
      this.cartReady=true;
      this.savedCodes=codesIn(cart);
      this.querySelectorAll('[data-offer-apply]').forEach(button=>{
        const applied=isApplied(cart,button.dataset.offerApply);
        const selected=isSelected(cart,button.dataset.offerApply);
        button.dataset.applied=String(applied);
        button.dataset.selected=String(selected);
        if(button.type==='checkbox')button.checked=selected;
        else button.textContent=selected ? this.text.applied : this.text.apply;
      });
      for(const [code,issue] of this.errors){
        if(issue.action==='sync'||isSelected(cart,code)===(issue.action==='apply'))this.errors.delete(code);
      }
      this.updateControls();
      if(this.applyDialog?.open&&isSelected(cart,this.lastCode)){
        this.applyDialog.close();
        this.applyDialog=null;
      }
    }
    scheduleRefresh() {clearTimeout(this.refreshTimer);this.refreshTimer=setTimeout(()=>this.refresh(),180);}
    async refresh() {
      if(this.busy || this.checkoutBusy || !this.isConnected) return;
      const revision=this.refreshRevision=(this.refreshRevision || 0)+1;
      const cartRevision=this.cartRevision;
      const connection=this.connectionRevision;
      const current=()=>!this.busy&&revision===this.refreshRevision&&cartRevision===this.cartRevision&&this.isConnected&&connection===this.connectionRevision;
      this.syncing=true;
      this.updateControls();
      try {
        const cart=await request(root.fetch.bind(root),this.base+'cart.js',undefined,AbortSignal.timeout(this.reconcileAction?6000:12000));
        if(current()){
          this.syncing=false;
          this.pendingCode=null;
          this.renderCart(cart);
          if(this.reconcileAction){
            const {code,removing}=this.reconcileAction;
            if(isSelected(cart,code)===removing)this.setError(code,removing?'removeError':'applyError',removing?'remove':'apply');
            else this.errors.delete(normalize(code));
            this.reconcileAction=null;
          }
          this.updateControls();
          if(this.lastCode){
            const applied=isApplied(cart,this.lastCode);
            const selected=isSelected(cart,this.lastCode);
            const result=this.lastAction==='remove'?(!selected?'removed':'remove-failed'):selected?'selected':'ineligible';
            if(result!==this.lastResult)this.announce(result==='removed'?'':result==='remove-failed'?this.text.error:result==='selected'?(applied?(this.lastBeforeCart&&discountsChanged(this.lastBeforeCart,cart)?this.text.appliedChanged:this.text.success):this.text.pending):this.text.ineligible);
            this.lastResult=result;
          }
          if(this.pendingSections){
            const definitions=this.pendingSections;
            const ids=[...new Set(definitions.map(def=>def.section||def.id))];
            if(!ids.length){this.pendingSections=null;return;}
            const response=await root.fetch(this.base+'?sections='+encodeURIComponent(ids.join(',')),{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(12000)});
            if(!response.ok)throw new Error('cart_sections_failed');
            const sections=await response.json();
            if(current()){
              this.renderSections(sections,definitions);
              this.pendingSections=null;
            }
          }
        }
      } catch(error) {
        if(current()){
          this.syncing=false;
          this.pendingCode=null;
          if(!this.cartReady){
            const code=this.reconcileAction?.code || this.lastCode;
            if(code)this.setError(code,error.status===429?'rateLimit':this.reconcileAction?'confirmError':'loadError',this.reconcileAction?(this.reconcileAction.removing?'remove':'apply'):'sync');
            else this.querySelectorAll('[data-offer-apply]').forEach(item=>this.setError(item.dataset.offerApply,error.status===429?'rateLimit':'loadError','sync'));
            this.announce(this.errorMessage(code || this.querySelector('[data-offer-apply]')?.dataset.offerApply));
          } else if(this.pendingSections)this.announce(this.text.error);
          this.updateControls();
        }
      } finally {
        if(revision===this.refreshRevision&&connection===this.connectionRevision){
          this.syncing=false;
          this.updateControls();
          if(cartRevision!==this.cartRevision&&!this.busy&&this.isConnected)this.scheduleRefresh();
        }
      }
    }
  }
  root.customElements.define('lr-product-offers',ProductOffers);
})(typeof window === 'undefined' ? globalThis : window);
